import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import PropTypes from 'prop-types';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Enhanced error logging
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', errorReport);
    }

    // In production, you would send this to your error monitoring service
    // this.sendErrorReport(errorReport);
  }

  getCurrentUserId = () => {
    try {
      // Safely get user ID without importing auth service to avoid circular deps
      const user = JSON.parse(sessionStorage.getItem('user') || 'null');
      return user?.id || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  sendErrorReport = (errorReport) => {
    // Placeholder for error reporting service
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorReport)
    // }).catch(() => {}); // Silently fail if error reporting fails
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="error-content"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={{ scale: 1 }}
              className="error-icon"
              initial={{ scale: 0 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <AlertTriangle size={48} color="var(--danger)" />
            </motion.div>

            <h2>Something went wrong</h2>
            <p>We&apos;re sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.</p>
            <div className="error-actions">
              <motion.button
                className="btn-secondary"
                onClick={() => window.location.reload()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Refresh Page
              </motion.button>
            </div>

            <motion.button
              className="btn-primary"
              onClick={this.handleRetry}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw size={16} />
              Try Again
            </motion.button>

            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <pre>{this.state.error && this.state.error.toString()}</pre>
                <pre>{this.state.errorInfo && this.state.errorInfo.componentStack}</pre>
              </details>
            )}
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
